import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Check, X, Pencil, Trash2, CalendarClock, History, UserX, Building2, Clock as ClockIcon, Search, RotateCcw } from "lucide-react";
import QuickSearchBar from "../components/QuickSearchBar";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import ExportCsvButton from "../components/ExportCsvButton";
import StickyPageHeader from "../components/StickyPageHeader";

const COLORS = ["#0055FF", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0EA5E9", "#EC4899", "#71717A"];

const STATUS_BADGES = {
  en_attente: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", label: "En attente" },
  approuve: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "Approuvé" },
  refuse: { bg: "bg-red-50 border-red-200", text: "text-red-700", label: "Refusé" },
};

export default function Indisponibilites() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [unavCats, setUnavCats] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [collabFilter, setCollabFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  // Recherche rapide pour l'onglet « Historique des absences »
  const [absFilters, setAbsFilters] = useState({ q: "", brand_id: "", date_from: "", date_to: "" });
  const [openReq, setOpenReq] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [openCat, setOpenCat] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [form, setForm] = useState({
    collaborateur_id: user?.id || "",
    brand_id: "",
    category_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [catForm, setCatForm] = useState({ name: "", description: "", color: "#0055FF" });

  const canDecide = user?.role === "admin" || user?.role === "manager";
  const canManageCat = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const isCollab = user?.role === "collaborateur" || user?.role === "gestionnaire";

  // Sépare les demandes classiques (kind ≠ auto_absence) et les absences déclarées d'office.
  const requestsListRaw = useMemo(() => list.filter((l) => l.kind !== "auto_absence"), [list]);

  // Filtres rapides appliqués à la liste des demandes
  const requestsList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return requestsListRaw.filter((l) => {
      if (collabFilter !== "all" && l.collaborateur_id !== collabFilter) return false;
      if (categoryFilter !== "all" && l.category_id !== categoryFilter) return false;
      // Période : demande chevauche [periodStart, periodEnd]
      if (periodStart && (l.end_date || "") < periodStart) return false;
      if (periodEnd && (l.start_date || "") > periodEnd) return false;
      if (q) {
        const u = users.find((x) => x.id === l.collaborateur_id);
        const cat = unavCats.find((x) => x.id === l.category_id);
        const haystack = [
          u?.name,
          u?.email,
          cat?.name,
          l.reason,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [requestsListRaw, searchQuery, collabFilter, categoryFilter, periodStart, periodEnd, users, unavCats]);

  const hasActiveFilters =
    !!searchQuery.trim() || collabFilter !== "all" || categoryFilter !== "all" || !!periodStart || !!periodEnd;

  const resetQuickFilters = () => {
    setSearchQuery("");
    setCollabFilter("all");
    setCategoryFilter("all");
    setPeriodStart("");
    setPeriodEnd("");
  };

  const absencesListRaw = useMemo(
    () =>
      list
        .filter((l) => l.kind === "auto_absence")
        .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || "")),
    [list],
  );
  const brandsMap = useMemo(() => {
    const m = {};
    (brands || []).forEach((b) => { m[b.id] = b; });
    return m;
  }, [brands]);

  const absencesList = useMemo(() => {
    const q = (absFilters.q || "").trim().toLowerCase();
    return absencesListRaw.filter((l) => {
      if (absFilters.brand_id && l.brand_id !== absFilters.brand_id) return false;
      if (absFilters.date_from && (l.start_date || "") < absFilters.date_from) return false;
      if (absFilters.date_to && (l.start_date || "") > absFilters.date_to) return false;
      if (!q) return true;
      const u = users.find((x) => x.id === l.collaborateur_id);
      const b = brandsMap[l.brand_id];
      const haystack = [
        u?.name,
        u?.email,
        b?.name,
        l.reason,
        l.start_date,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [absencesListRaw, absFilters, users, brandsMap]);

  const totalDays = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    try {
      const d1 = new Date(`${form.start_date}T00:00:00`);
      const d2 = new Date(`${form.end_date}T00:00:00`);
      if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return 0;
      const diff = Math.round((d2 - d1) / 86400000) + 1;
      return diff > 0 ? diff : 0;
    } catch {
      return 0;
    }
  }, [form.start_date, form.end_date]);

  const pendingCount = useMemo(() => list.filter((l) => l.status === "en_attente").length, [list]);

  const load = async () => {
    const params = filter !== "all" ? { status: filter } : {};
    if (selectedBrandId) params.brand_id = selectedBrandId;
    const collabParams = selectedBrandId ? { brand_id: selectedBrandId } : {};
    const [l, u, c] = await Promise.all([
      api.get("/leaves", { params }),
      api.get("/collaborateurs", { params: collabParams }),
      api.get("/unavailability-categories"),
    ]);
    setList(l.data || []);
    setUsers(u.data || []);
    setUnavCats(c.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, selectedBrandId]);

  useEffect(() => {
    if (user?.id) setForm((f) => ({ ...f, collaborateur_id: user.id }));
  }, [user]);

  // Auto-ouverture de la modale "Nouvelle demande" depuis le tableau de bord (?new=1)
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditingLeave(null);
      setOpenReq(true);
      params.delete("new");
      const rest = params.toString();
      navigate(`${location.pathname}${rest ? `?${rest}` : ""}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const submit = async () => {
    try {
      if (!form.start_date || !form.end_date || !form.category_id) {
        toast.error("Catégorie et dates requises");
        return;
      }
      if (editingLeave) {
        await api.put(`/leaves/${editingLeave.id}`, { ...form, brand_id: form.brand_id || null });
        toast.success("Demande mise à jour");
      } else {
        await api.post("/leaves", { ...form, brand_id: form.brand_id || null });
        toast.success("Demande envoyée");
      }
      setOpenReq(false);
      setEditingLeave(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openEditLeave = (l) => {
    setEditingLeave(l);
    setForm({
      collaborateur_id: l.collaborateur_id,
      brand_id: l.brand_id || "",
      category_id: l.category_id || "",
      start_date: l.start_date || "",
      end_date: l.end_date || "",
      reason: l.reason || "",
    });
    setOpenReq(true);
  };

  const deleteLeave = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success("Demande supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteAbsence = async (l) => {
    const u = users.find((x) => x.id === l.collaborateur_id);
    const who = u?.name || "—";
    const when = formatDateFr(l.start_date);
    if (!window.confirm(
      `Supprimer définitivement l'absence auto-déclarée de ${who} (${when}) ?\n\n`
      + "Cette action est irréversible. L'entrée disparaîtra de l'historique."
    )) return;
    try {
      await api.delete(`/leaves/${l.id}`);
      toast.success("Absence supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const decide = async (id, status) => {
    try {
      await api.put(`/leaves/${id}/decision`, { status, decision_note: "" });
      toast.success(status === "approuve" ? "Demande approuvée" : "Demande refusée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const saveCat = async () => {
    try {
      if (!catForm.name.trim()) {
        toast.error("Nom requis");
        return;
      }
      if (editingCat) {
        await api.put(`/unavailability-categories/${editingCat.id}`, catForm);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/unavailability-categories", catForm);
        toast.success("Catégorie créée");
      }
      setOpenCat(false);
      setEditingCat(null);
      setCatForm({ name: "", description: "", color: "#0055FF" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeCat = async (id) => {
    if (!window.confirm("Supprimer cette catégorie d'indisponibilité ?")) return;
    await api.delete(`/unavailability-categories/${id}`);
    toast.success("Supprimée");
    load();
  };

  const openCreateCat = () => {
    setEditingCat(null);
    setCatForm({ name: "", description: "", color: "#0055FF" });
    setOpenCat(true);
  };

  const openEditCat = (c) => {
    setEditingCat(c);
    setCatForm({ name: c.name, description: c.description || "", color: c.color });
    setOpenCat(true);
  };

  return (
    <div className="space-y-6" data-testid="indispo-page">
      <Tabs defaultValue="demandes" className="w-full">
        <StickyPageHeader testid="indispo-sticky-header">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-uppercase mb-2">Workflow</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
              Indisponibilités
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">Congés, maladie, autres absences</p>
          </div>
          {canDecide && pendingCount > 0 && (
            <div
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-200 bg-amber-50"
              data-testid="leaves-pending-badge"
            >
              <CalendarClock size={14} className="text-amber-700" />
              <span className="text-sm font-medium text-amber-900">
                {pendingCount} demande{pendingCount > 1 ? "s" : ""} en attente
              </span>
            </div>
          )}
        </div>

        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-white border border-zinc-200 rounded-sm p-0 h-auto w-max">
            <TabsTrigger value="demandes" className="rounded-sm px-4 py-2 data-[state=active]:bg-zinc-100" data-testid="tab-demandes">Historique demandes</TabsTrigger>
            <TabsTrigger value="absences" className="rounded-sm px-4 py-2 data-[state=active]:bg-zinc-100" data-testid="tab-absences-history">
              <span className="inline-flex items-center gap-1.5">
                <UserX size={13} className="text-red-600" />
                Historique des absences
                {absencesList.length > 0 && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200">
                    {absencesList.length}
                  </span>
                )}
              </span>
            </TabsTrigger>
            {canManageCat && (
              <TabsTrigger value="categories" className="rounded-sm px-4 py-2 data-[state=active]:bg-zinc-100" data-testid="tab-categories-indispo">Catégories</TabsTrigger>
            )}
          </TabsList>
        </div>
        </StickyPageHeader>

        <TabsContent value="demandes" className="mt-6 space-y-4">
          {/* Barre de recherche rapide + filtres */}
          <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="leaves-quick-filters">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="label-uppercase flex items-center gap-1.5">
                <Search size={12} /> Recherche rapide
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetQuickFilters}
                  className="text-[11px] uppercase font-semibold tracking-wider text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
                  data-testid="reset-quick-filters"
                >
                  <RotateCcw size={11} /> Réinitialiser
                </button>
              )}
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isCollab ? "lg:grid-cols-4" : "lg:grid-cols-5"}`}>
              <div className="lg:col-span-2 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Nom, email, catégorie ou motif…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="leaves-search-input"
                />
              </div>
              {!isCollab && (
                <Select value={collabFilter} onValueChange={setCollabFilter}>
                  <SelectTrigger data-testid="leaves-collab-filter">
                    <SelectValue placeholder="Collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les collaborateurs</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="leaves-category-filter">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {unavCats.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  aria-label="Période — début"
                  title="Période — début"
                  data-testid="leaves-period-start"
                />
                <Input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  aria-label="Période — fin"
                  title="Période — fin"
                  data-testid="leaves-period-end"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <p className="text-[11px] text-zinc-500 font-mono" data-testid="leaves-filter-count">
                {requestsList.length} résultat{requestsList.length > 1 ? "s" : ""} sur {requestsListRaw.length}
              </p>
            )}
            <div className="flex justify-end">
              <ExportCsvButton
                filename="indisponibilites_demandes"
                testid="leaves-export-csv"
                rows={requestsList}
                columns={[
                  { label: "Collaborateur", value: (l) => users.find((x) => x.id === l.collaborateur_id)?.name || "" },
                  { label: "Email", value: (l) => users.find((x) => x.id === l.collaborateur_id)?.email || "" },
                  { label: "Département", value: (l) => brands.find((x) => x.id === l.brand_id)?.name || "" },
                  { label: "Catégorie", value: (l) => unavCats.find((x) => x.id === l.category_id)?.name || "" },
                  { label: "Début", value: (l) => l.start_date || "" },
                  { label: "Fin", value: (l) => l.end_date || "" },
                  { label: "Statut", value: (l) => (STATUS_BADGES[l.status]?.label || l.status || "") },
                  { label: "Motif", value: (l) => l.reason || "" },
                ]}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les demandes</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="approuve">Approuvées</SelectItem>
                <SelectItem value="refuse">Refusées</SelectItem>
              </SelectContent>
            </Select>
            {(canDecide || isCollab) && (
            <Dialog open={openReq} onOpenChange={(v) => { setOpenReq(v); if (!v) setEditingLeave(null); }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingLeave(null); setForm({ collaborateur_id: user?.id || "", brand_id: "", category_id: "", start_date: "", end_date: "", reason: "" }); }} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-leave-btn">
                  <Plus size={14} className="mr-1" /> {isCollab ? "Nouvelle demande" : "Nouvelle indisponibilité"}
                </Button>
              </DialogTrigger>
              <DialogContent className="flex flex-col max-h-[90vh] p-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                  <DialogTitle>
                    {editingLeave
                      ? "Modifier la demande"
                      : isCollab
                        ? "Demande d'indisponibilité"
                        : "Déclarer une indisponibilité"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="leave-dialog-scroll">
                  {canDecide && (
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Collaborateur</Label>
                      <Select value={form.collaborateur_id} onValueChange={(v) => setForm({ ...form, collaborateur_id: v })}>
                        <SelectTrigger data-testid="leave-collab"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`leave-collab-opt-${u.id}`}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Motif</Label>
                    <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                      <SelectTrigger data-testid="leave-cat"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                      <SelectContent>
                        {unavCats.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`leave-cat-opt-${c.id}`}>
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                              {c.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Du</Label>
                      <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Au</Label>
                      <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" />
                    </div>
                  </div>
                  {totalDays > 0 && (
                    <div className="bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 flex items-center gap-2 text-sm" data-testid="leave-total-days">
                      <CalendarClock size={14} className="text-sky-600" />
                      <span className="text-sky-900 font-medium">
                        Durée totale : {totalDays} jour{totalDays > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                  {canDecide && (
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Département (optionnel)</Label>
                      <Select value={form.brand_id || "none"} onValueChange={(v) => setForm({ ...form, brand_id: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Toutes" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Toutes</SelectItem>
                          {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="label-uppercase mb-1.5 block">{isCollab ? "Commentaire (optionnel)" : "Motif"}</Label>
                    <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} data-testid="leave-reason" />
                  </div>
                  {isCollab && !editingLeave && (
                    <p className="text-xs text-zinc-500 font-mono">
                      Votre demande sera transmise à votre manager et aux administrateurs pour validation.
                    </p>
                  )}
                </div>
                <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                  <Button variant="outline" onClick={() => setOpenReq(false)}>Annuler</Button>
                  <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="leave-save-btn">{editingLeave ? "Mettre à jour" : "Envoyer"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <HorizontalScrollContainer testId="indispo-requests-scroll" className="bg-white border border-zinc-200 rounded-sm">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left label-uppercase p-3">Collaborateur</th>
                  <th className="text-left label-uppercase p-3">Catégorie</th>
                  <th className="text-left label-uppercase p-3">Période</th>
                  <th className="text-left label-uppercase p-3">Statut</th>
                  <th className="text-left label-uppercase p-3">Motif</th>
                  {canDecide && <th className="text-right label-uppercase p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {requestsList.map((l) => {
                  const u = users.find((x) => x.id === l.collaborateur_id);
                  const cat = unavCats.find((x) => x.id === l.category_id);
                  const badge = STATUS_BADGES[l.status] || STATUS_BADGES.en_attente;
                  return (
                    <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`leave-${l.id}`}>
                      <td className="p-3 text-sm font-medium text-zinc-900">{u?.name || "—"}</td>
                      <td className="p-3 text-sm text-zinc-700">
                        {cat ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="p-3 font-mono text-xs text-zinc-600">{formatDateFr(l.start_date)} → {formatDateFr(l.end_date)}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-zinc-700 max-w-xs truncate">{l.reason || "—"}</td>
                      {canDecide && (
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            {l.status === "en_attente" && (
                              <>
                                <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => decide(l.id, "approuve")} data-testid={`approve-${l.id}`}>
                                  <Check size={12} />
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => decide(l.id, "refuse")} data-testid={`refuse-${l.id}`}>
                                  <X size={12} />
                                </Button>
                              </>
                            )}
                            {isAdmin && (
                              <>
                                <Button variant="outline" size="sm" className="text-zinc-700 border-zinc-200 hover:bg-zinc-50" onClick={() => openEditLeave(l)} data-testid={`edit-leave-${l.id}`} title="Modifier">
                                  <Pencil size={12} />
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => deleteLeave(l.id)} data-testid={`delete-leave-${l.id}`} title="Supprimer">
                                  <Trash2 size={12} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {requestsList.length === 0 && (
                  <tr><td colSpan={6} className="text-center p-8 text-sm text-zinc-500 font-mono">Aucune demande</td></tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </TabsContent>

        <TabsContent value="absences" className="mt-6 space-y-4">
          <QuickSearchBar
            value={absFilters}
            onChange={setAbsFilters}
            placeholder="Rechercher une absence (collaborateur, email, département, détails)…"
            testid="absences-search"
            filters={[
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: (brands || []).map((b) => ({ value: b.id, label: b.name })) },
              { key: "date_from", label: "Du", type: "date" },
              { key: "date_to", label: "Au", type: "date" },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="absences-result-count">
              {absencesList.length} absence{absencesList.length > 1 ? "s" : ""} auto-déclarée{absencesList.length > 1 ? "s" : ""}
              {absencesList.length !== absencesListRaw.length && ` sur ${absencesListRaw.length}`}
            </p>
            <ExportCsvButton
              filename="absences_auto_declarees"
              testid="absences-export-csv"
              rows={absencesList}
              columns={[
                { label: "Collaborateur", value: (l) => users.find((x) => x.id === l.collaborateur_id)?.name || "" },
                { label: "Email", value: (l) => users.find((x) => x.id === l.collaborateur_id)?.email || "" },
                { label: "Date d'absence", value: (l) => l.start_date || "" },
                { label: "Département", value: (l) => brandsMap[l.brand_id]?.name || "" },
                { label: "Détails", value: (l) => l.reason || "" },
                { label: "Déclarée le", value: (l) => (l.decided_at ? String(l.decided_at).slice(0, 10) : "") },
              ]}
            />
          </div>
          <HorizontalScrollContainer testId="absences-history-table" className="bg-white border border-zinc-200 rounded-sm">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left label-uppercase p-3">Collaborateur</th>
                  <th className="text-left label-uppercase p-3">Date d'absence</th>
                  <th className="text-left label-uppercase p-3">Département</th>
                  <th className="text-left label-uppercase p-3">Détails</th>
                  <th className="text-left label-uppercase p-3">Déclarée le</th>
                  {isAdmin && <th className="text-right label-uppercase p-3">Action</th>}
                </tr>
              </thead>
              <tbody>
                {absencesList.map((l) => {
                  const u = users.find((x) => x.id === l.collaborateur_id);
                  const b = brandsMap[l.brand_id];
                  return (
                    <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`absence-row-${l.id}`}>
                      <td className="p-3 text-sm font-medium text-zinc-900">
                        <span className="inline-flex items-center gap-1.5">
                          <UserX size={13} className="text-red-600" />
                          {u?.name || "—"}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs text-zinc-700">{formatDateFr(l.start_date)}</td>
                      <td className="p-3 text-sm text-zinc-700">
                        {b ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <Building2 size={11} className="text-zinc-400" />
                            {b.name}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="p-3 text-sm text-zinc-700 max-w-md">
                        <p className="line-clamp-2">{l.reason || "—"}</p>
                        <p className="text-[11px] text-zinc-500 font-mono mt-0.5 inline-flex items-center gap-1">
                          <ClockIcon size={10} /> Auto-déclarée (non pointage)
                        </p>
                      </td>
                      <td className="p-3 font-mono text-xs text-zinc-500">{l.decided_at ? formatDateFr(l.decided_at.slice(0, 10)) : "—"}</td>
                      {isAdmin && (
                        <td className="p-3 text-right">
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => deleteAbsence(l)}
                            data-testid={`absence-delete-btn-${l.id}`}
                            title="Supprimer cette absence"
                          >
                            <Trash2 size={12} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {absencesList.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="text-center p-8 text-sm text-zinc-500 font-mono">
                    <span className="inline-flex items-center gap-2">
                      <History size={14} className="text-zinc-400" />
                      Aucune absence enregistrée
                    </span>
                  </td></tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
          <p className="text-xs text-zinc-500 font-mono">
            Cet historique liste les absences déclarées par un responsable suite à un non-pointage. Les entrées sont créées automatiquement depuis le tableau de bord (Retards de pointage → Collaborateur absent).
          </p>
        </TabsContent>

        {canManageCat && (
          <TabsContent value="categories" className="mt-6 space-y-4">
          {canManageCat && (
            <div className="flex justify-end">
              <Dialog open={openCat} onOpenChange={setOpenCat}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateCat} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-indispo-cat-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle catégorie
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{editingCat ? "Modifier la catégorie" : "Nouvelle catégorie d'indisponibilité"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="indispo-cat-dialog-scroll">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Nom</Label>
                      <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Ex: Récupération" data-testid="indispo-cat-name" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setCatForm({ ...catForm, color: c })}
                            className={`h-9 w-9 rounded-sm border-2 ${catForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setOpenCat(false)}>Annuler</Button>
                    <Button onClick={saveCat} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="indispo-cat-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {unavCats.map((c) => (
              <div key={c.id} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300" data-testid={`indispo-cat-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className="h-6 w-1 rounded-sm" style={{ backgroundColor: c.color }} />
                  {canManageCat && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditCat(c)}>
                        <Pencil size={12} />
                      </Button>
                      {user?.role === "admin" && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeCat(c.id)}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold text-zinc-900">{c.name}</h3>
                <p className="text-sm text-zinc-600 mt-1">{c.description || "—"}</p>
              </div>
            ))}
            {unavCats.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                Aucune catégorie
              </div>
            )}
          </div>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
